import {
  DataTable,
  Dialog,
  FormInput,
  FormLabel,
  FormSelect,
  Heading,
  Loader,
} from "@/components";
import useListing from "./useListing";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui";
import { FormProvider } from "react-hook-form";
import { monthOptions } from "@/lib/utils";
import {
  frequencyOptions,
  postDocumentSearch,
  transactionTypeOptions,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
} from "../../consts";

const Listing = () => {
  const {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    onRowClick,
    isDocLoading,
    documentList,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    isDocRefetching,
    form,
    onSubmit,
    requiredFields,
    onClear,
    communityDetail,
    onMonthChange,
    monthKey,
    month,
    isLoadingBillingCategory,
    billingCategory,
    onBackClick,
    currentFinanceYear,
    openReverseDialog,
    setOpenReverseDialog,
    onReverseBilling,
    onCommentChange,
    isReversingPostedDoc,
    sorting,
    setSorting,
  } = useListing();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      {/* Header & Search */}
      <div className="flex flex-col xl:flex-row gap-2 xl:items-center xl:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title={<span>Billed for the Period - {communityDetail?.communityName} <span className="text-[12px]">(FY-{currentFinanceYear})</span></span>}
            breadcrumbData={breadcrumbData}
            showBack
            onBackClick={onBackClick}
          />
          {/* {isMobile && <Button className="px-2">+ Add Budget</Button>} */}
        </div>
        <div className="relative mb-4 md:w-1/5 lg:w-1/10 mt-2">
          <FormLabel>Month</FormLabel>
          <Select onValueChange={onMonthChange} key={monthKey}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={
                  monthOptions?.find((data: any) => data.value == month)?.label
                }
              />
            </SelectTrigger>
            <SelectContent>
              {monthOptions?.map((data: any) => (
                <SelectItem key={data?.value} value={data?.value}>
                  {data?.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoadingBillingCategory ? (
        <></>
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px] mb-[30px]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                <FormSelect
                  name={SEARCH_TYPE}
                  control={form.control}
                  label="Search Type"
                  placeholder="Please select"
                  error={errors[SEARCH_TYPE]?.message}
                  isRequired={requiredFields.includes(SEARCH_TYPE)}
                  options={[...postDocumentSearch, ...searchType]}
                />
                {form.watch(SEARCH_TYPE) == "billingCategoryIds" ? (
                  <FormSelect
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Billing Category"
                    placeholder="Please select"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                    options={(billingCategory?.data || [])?.map(
                      (data: any) => ({
                        value: data?.billingCategoryId?.toString(),
                        label: data?.categoryName,
                      })
                    )}
                  />
                ) : form.watch(SEARCH_TYPE) == "transactionType" ? (
                  <FormSelect
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Transaction Type"
                    placeholder="Please select"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                    options={transactionTypeOptions}
                  />
                ) : form.watch(SEARCH_TYPE) == "frequency" ? (
                  <FormSelect
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Frequency"
                    placeholder="Please select"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                    options={frequencyOptions}
                  />
                ) : (
                  <FormInput
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Search Criteria"
                    placeholder="Please enter"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                  />
                )}

                <div className="flex flex-row justify-end gap-3 pt-3">
                  <Button type="submit">Search</Button>
                  <Button variant="outline" type="button" onClick={onClear}>
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      )}

      {/* Responsive Table */}
      {isDocLoading || isDocRefetching || isLoadingBillingCategory ? (
        <Loader />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={documentList?.data || []}
            setGlobalFilter={setGlobalFilter}
            globalFilter={globalFilter}
            onRowClick={onRowClick}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalPages={Math.ceil(documentList?.total / pageSize)}
            totalRecords={documentList?.total}
            serverSidePagination={true}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </>
      )}
      {openReverseDialog && (
        <Dialog
          open={openReverseDialog}
          setOpen={setOpenReverseDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              Reverse Billing Document
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Yes"
          onSave={onReverseBilling}
          contentClassName="md:w-[800px]"
          isLoading={isReversingPostedDoc}
        >
          <span className="text-[14px]">
            You are about to reverse this billing document. <br />
            All associated line items, GL postings, and budget consumption
            records will be reversed. This action is irreversible.
            <br />
            <br /> Please provide a reason for the reversal in the comments box
            below. <br />
            <br />
          </span>
          <div className="relative">
            <FormLabel>
              Comment <span className="text-red-600">*</span>
            </FormLabel>
            <Textarea placeholder="Please enter" onChange={onCommentChange} />
          </div>{" "}
          <br />
          <b>Are you sure you want to proceed?</b>
        </Dialog>
      )}
    </div>
  );
};

export default Listing;
